import { all, fork, put, takeEvery, call } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";

// helpers
import {
  getShippings as getShippingList,
  getShipping as getShippingApi,
  updateShippingApi,
  deleteShippingApi,
  updateStatusShippingApi,
  updateDeliveryShippingApi,
  downloadShippingsList as downloadShippingsListApi,
} from "../../helpers/";

// constants
import { ShippingsActionTypes } from "./constants";
import {
  ShippingsGetListError,
  ShippingsGetListSuccess,
  getShippingSuccess,
  getShippingError,
  updateShippingSuccess,
  updateShippingError,
  deleteShippingSuccess,
  deleteShippingError,
  updateStatusShippingSuccess,
  updateStatusShippingError,
  updateDeliveryShippingSuccess,
  updateDeliveryShippingError,
  downloadShippingsListSuccess,
  downloadShippingsListError,
} from "./actions";

interface ShippingData {
  payload: {
    limit: number;
    skip: number;
    keyword?: string;
    owner?: string;
    deliveryGuy?: string;
    country?: string;
    status?: string;
    product?: string;
    boutique?: string;
  };
  type: string;
}

interface ShippingUpdateData {
  payload: {
    id: string;
    product: string;
    customerName: string;
    customerPhone: string;
    customerAddress: string;
    price: number;
    quantity: number;
    sourceLink: string;
    sourceName: string;
    totalPrice: number;
  };
  type: string;
}

interface ShippingStatusUpdateData {
  payload: {
    id: string;
    status: string;
    comment: string;
    cashAmount?: number;
    cashCurrency?: string;
    podPhotoUrl?: string;
    podSignatureUrl?: string;
    receivedBy?: string;
    location?: { lat?: number; lng?: number };
    outcome?: string;
    reprogrammedFor?: string;
  };
  type: string;
}

interface ShippingDeliveryUpdateData {
  payload: {
    id: string;
    delivery: string;
  };
  type: string;
}

interface ShippingDeleteData {
  payload: {
    ids: string[];
  };
  type: string;
}

interface GetShippingData {
  payload: {
    id: string;
  };
  type: string;
}

interface DownloadShippingsListData {
  payload: {
    keyword?: string;
    owner?: string;
    deliveryGuy?: string;
    deliveryGuyName?: string;
    country?: string;
    status?: string;
    product?: string;
  };
  type: string;
}

function* getShippings({
  payload: { limit, skip, keyword, owner, deliveryGuy, country, status, product, boutique },
}: ShippingData): SagaIterator {
  try {
    const response = yield call(getShippingList, {
      limit,
      skip,
      keyword,
      owner,
      deliveryGuy,
      country,
      status,
      product,
      boutique,
    });
    // Voir orders/saga : ignore 403 silencieux + data null pour ne pas
    // écraser la liste courante.
    if ((response as any)?.__handled403) return;
    const Shippings = response.data;
    if (Shippings == null) return;
    yield put(
      ShippingsGetListSuccess(
        ShippingsActionTypes.GET_LIST_SHIPPINGS,
        Shippings
      )
    );
  } catch (error: any) {
    yield put(
      ShippingsGetListError(ShippingsActionTypes.GET_LIST_SHIPPINGS, error)
    );
  }
}

function* updateShipping({
  payload: {
    id,
    product,
    customerName,
    customerPhone,
    customerAddress,
    price,
    quantity,
    sourceLink,
    sourceName,
    totalPrice,
  },
}: ShippingUpdateData): SagaIterator {
  try {
    const response = yield call(updateShippingApi, {
      id,
      product,
      customerName,
      customerPhone,
      customerAddress,
      price,
      quantity,
      sourceLink,
      sourceName,
      totalPrice,
    });
    const Shipping = response.data;
    yield put(
      updateShippingSuccess(ShippingsActionTypes.UPDATE_SHIPPING, Shipping)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      updateShippingError(ShippingsActionTypes.UPDATE_SHIPPING, errorMessage)
    );
  }
}

function* updateStatusShipping({
  payload: {
    id,
    status,
    comment,
    cashAmount,
    cashCurrency,
    podPhotoUrl,
    podSignatureUrl,
    receivedBy,
    location,
    outcome,
    reprogrammedFor,
  },
}: ShippingStatusUpdateData): SagaIterator {
  try {
    const response = yield call(updateStatusShippingApi, {
      id,
      status,
      comment,
      cashAmount,
      cashCurrency,
      podPhotoUrl,
      podSignatureUrl,
      receivedBy,
      location,
      outcome,
      reprogrammedFor,
    });
    const Shipping = response.data;
    yield put(
      updateStatusShippingSuccess(
        ShippingsActionTypes.UPDATE_STATUS_SHIPPING,
        Shipping
      )
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      updateStatusShippingError(
        ShippingsActionTypes.UPDATE_STATUS_SHIPPING,
        errorMessage
      )
    );
  }
}

function* updateDeliveryShipping({
  payload: { id, delivery },
}: ShippingDeliveryUpdateData): SagaIterator {
  try {
    const response = yield call(updateDeliveryShippingApi, {
      id,
      delivery,
    });
    const Shipping = response.data;
    yield put(
      updateDeliveryShippingSuccess(
        ShippingsActionTypes.UPDATE_DELIVERY_SHIPPING,
        Shipping
      )
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      updateDeliveryShippingError(
        ShippingsActionTypes.UPDATE_DELIVERY_SHIPPING,
        errorMessage
      )
    );
  }
}

function* deleteShipping({
  payload: { ids },
}: ShippingDeleteData): SagaIterator {
  try {
    const response = yield call(deleteShippingApi, {
      ids,
    });
    const Shipping = response.data;
    yield put(
      deleteShippingSuccess(ShippingsActionTypes.DELETE_SHIPPING, Shipping)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      deleteShippingError(ShippingsActionTypes.DELETE_SHIPPING, errorMessage)
    );
  }
}

function* getShipping({ payload: { id } }: GetShippingData): SagaIterator {
  try {
    const response = yield call(getShippingApi, { id });
    const Shipping = response.data;
    yield put(getShippingSuccess(ShippingsActionTypes.GET_SHIPPING, Shipping));
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      getShippingError(ShippingsActionTypes.GET_SHIPPING, errorMessage)
    );
  }
}

function* downloadShippingsList({
  payload: { keyword, owner, deliveryGuy, deliveryGuyName, country, status, product },
}: DownloadShippingsListData): SagaIterator {
  try {
    const response = yield call(downloadShippingsListApi, {
      keyword,
      owner,
      deliveryGuy,
      deliveryGuyName,
      country,
      status,
      product,
    });

    // Create a blob from the response
    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "Delivery-List.xlsx");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    yield put(
      downloadShippingsListSuccess(
        ShippingsActionTypes.DOWNLOAD_SHIPPINGS_LIST,
        response.data
      )
    );
  } catch (error: any) {
    yield put(
      downloadShippingsListError(
        ShippingsActionTypes.DOWNLOAD_SHIPPINGS_LIST,
        error
      )
    );
  }
}

// Add this watcher
export function* watchDownloadShippingsList() {
  yield takeEvery(
    ShippingsActionTypes.DOWNLOAD_SHIPPINGS_LIST,
    downloadShippingsList
  );
}

export function* watchGetShippings() {
  yield takeEvery(ShippingsActionTypes.GET_LIST_SHIPPINGS, getShippings);
}

export function* watchUpdateShippings() {
  yield takeEvery(ShippingsActionTypes.UPDATE_SHIPPING, updateShipping);
}

export function* watchUpdateStatusShippings() {
  yield takeEvery(
    ShippingsActionTypes.UPDATE_STATUS_SHIPPING,
    updateStatusShipping
  );
}

export function* watchUpdateDeliveryShippings() {
  yield takeEvery(
    ShippingsActionTypes.UPDATE_DELIVERY_SHIPPING,
    updateDeliveryShipping
  );
}

export function* watchDeleteShippings() {
  yield takeEvery(ShippingsActionTypes.DELETE_SHIPPING, deleteShipping);
}

export function* watchGetShipping() {
  yield takeEvery(ShippingsActionTypes.GET_SHIPPING, getShipping);
}

function* shippingsSaga() {
  yield all([
    fork(watchGetShippings),
    fork(watchGetShipping),
    fork(watchUpdateShippings),
    fork(watchDeleteShippings),
    fork(watchUpdateStatusShippings),
    fork(watchUpdateDeliveryShippings),
    fork(watchDownloadShippingsList),
  ]);
}

export default shippingsSaga;
